import React, { useState, useEffect } from 'react'

function formatCurrency(valor) {
  if (valor === undefined || valor === null || isNaN(valor)) return '-'
  return `$${Math.round(Number(valor)).toLocaleString('es-AR')}`
}

function formatPercent(valor) {
  if (valor === undefined || valor === null || isNaN(valor)) return '-'
  return `${(Number(valor) * 100).toFixed(1)}%`
}

function tieneDescuentos(escenario) {
  return (escenario.desglose_cuota_moratoria || 0) + (escenario.descuento_obra_social || 0) > 0
}

function formatFechaCese(escenario) {
  if (!escenario.fecha_cese) return 'Cese actual'
  const partes = escenario.fecha_cese.split('-')
  if (partes.length === 3) {
    return `${partes[1]}/${partes[0]}`
  }
  return escenario.fecha_cese
}

function esError(escenario) {
  return escenario.error === true || typeof escenario.error === 'string'
}

function obtenerMensajeError(escenario) {
  if (typeof escenario.error === 'string') return escenario.error
  if (escenario.mensajeError) return escenario.mensajeError
  return 'Error desconocido al procesar el escenario'
}

function obtenerTasa(escenario) {
  return escenario.haber?.tasaSustitucion ?? escenario.diagnostico?.tasaSustitucion ?? 0
}

/**
 * TablaComparativa - Visualiza resultados de escenarios previsionales
 * Compara: Haber Bruto, Descuentos, Haber Neto y Tasa de Sustitución
 * Resalta en verde el escenario con mayor Haber Neto.
 * 
 * Todos los valores vienen pre-calculados desde el motor.
 * Los nombres de propiedad coinciden exactamente con engine.js:
 *   nombre, haber_mensual (raíz), haber_neto (raíz),
 *   desglose_cuota_moratoria, descuento_obra_social,
 *   haber.tasaSustitucion, haber.haberTotal, diagnostico.esBaja
 */
export default function TablaComparativa({ escenarios = [], cajaOtorgante = 'SIPA' }) {
  if (!escenarios || escenarios.length === 0) return null

  const [sesionActiva, setSesionActiva] = useState(false)

  useEffect(() => {
    const userRef = localStorage.getItem('userRef') || ''
    setSesionActiva(sessionStorage.getItem(`sesionPagada_${userRef}`) === 'true')

    const onReset = () => setSesionActiva(false)
    window.addEventListener('sesion-pagada-reset', onReset)
    return () => window.removeEventListener('sesion-pagada-reset', onReset)
  }, [escenarios])

  const netosValidos = escenarios.filter(e => !esError(e)).map(e => Number(e.haber_neto ?? e.haber_mensual ?? 0))
  const maxNeto = Math.max(...netosValidos, 0)

  const celdaBruto = (escenario) => esError(escenario) ? '-' : formatCurrency(escenario.haber_mensual ?? escenario.haber?.haberTotal)
  const celdaNeto = (escenario) => esError(escenario) ? '-' : formatCurrency(escenario.haber_neto ?? escenario.haber_mensual)
  const celdaTasa = (escenario) => esError(escenario) ? '-' : formatPercent(obtenerTasa(escenario))

  const errores = escenarios.filter(esError)

  return (
    <div className="my-6 overflow-x-auto">
      {sesionActiva && (() => {
        const userRef = localStorage.getItem('userRef') || ''
        return (
          <div className="panel-profile-accent text-profile-accent mb-4 flex items-center rounded-md p-2 text-sm">
            ✅ Sesión de cálculo activa para el usuario {userRef}. Puede realizar ajustes y re-calcular sin costo adicional hasta descargar el reporte final.
          </div>
        )
      })()}
      <table className="table-contrast-grid w-full border-collapse shadow-lg">
        <thead>
          <tr>
            <th className="p-3 text-left font-semibold">
              Concepto
              <div className="panel-profile-accent text-profile-accent mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium">
                Caja: {cajaOtorgante}
              </div>
            </th>
            {escenarios.map((escenario, idx) => (
              <th key={idx} className="p-3 text-center font-semibold">
                {esError(escenario) && <span className="mr-1">⚠️</span>}
                {escenario.nombre || `Escenario ${idx + 1}`}
                <div className="mt-0.5 text-xs font-normal opacity-80">{formatFechaCese(escenario)}</div>
                {!esError(escenario) && (escenario.diagnostico?.esBaja || escenario.haber?.esConfiscatorio) && (
                  <span title="Tasa de sustitución con riesgo de confiscatoriedad" className="ml-1">⚠️</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Haber Bruto */}
          <tr className="border-b border-gray-200">
            <td className="p-3 font-medium">Haber Bruto</td>
            {escenarios.map((escenario, idx) => (
              <td key={idx} className="p-3 text-center">
                {celdaBruto(escenario)}
              </td>
            ))}
          </tr>

          {/* Descuentos — usan valores pre-calculados por el motor */}
          <tr className="border-b border-gray-200 bg-gray-50">
            <td className="p-3 font-medium">Descuentos</td>
            {escenarios.map((escenario, idx) => {
              const cuota = escenario.desglose_cuota_moratoria || 0
              const os = escenario.descuento_obra_social || 0
              const total = cuota + os
              return (
                <td key={idx} className="text-profile-danger p-3 text-center text-sm">
                  {total > 0 ? `-${formatCurrency(total)}` : '-'}
                  {cuota > 0 && (
                    <div className="text-profile-secondary mt-1 text-xs">(Incl. Moratoria)</div>
                  )}
                </td>
              )
            })}
          </tr>

          {/* Haber Neto — una fila con celdas por escenario */}
          <tr className="border-b border-gray-200">
            <td className="p-3 font-medium">Haber Neto</td>
            {escenarios.map((escenario, idx) => {
              const neto = Number(escenario.haber_neto ?? escenario.haber_mensual ?? 0)
              const esMaximo = neto >= maxNeto && maxNeto > 0
              return (
                <td
                  key={idx}
                  className={`p-3 text-center ${esMaximo ? 'panel-profile-ok text-profile-ok text-lg font-semibold' : ''}`}
                >
                  {formatCurrency(neto)}
                    {esMaximo && <span className="text-profile-ok ml-1">✓</span>}
                </td>
              )
            })}
          </tr>

          {/* Tasa de Sustitución */}
          <tr className="border-b border-gray-200 bg-gray-50">
            <td className="p-3 font-medium">Tasa de Sustitución</td>
            {escenarios.map((escenario, idx) => (
              <td key={idx} className="p-3 text-center">
                {celdaTasa(escenario)}
                {(escenario.diagnostico?.esBaja || escenario.haber?.esConfiscatorio) && (
                  <span className="ml-1" title="Tasa de sustitución con riesgo de confiscatoriedad">⚠️</span>
                )}
              </td>
            ))}
          </tr>

          {/* Errores — una fila por escenario fallido ocupando todo el ancho */}
          {errores.map((escenario, idx) => (
            <tr key={`err-${idx}`} className="border-t border-red-200">
              <td colSpan={escenarios.length + 1} className="panel-profile-danger text-profile-danger p-3 text-sm">
                <span className="font-semibold">⚠️ {escenario.nombre}:</span>{' '}
                {obtenerMensajeError(escenario)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="text-profile-secondary mt-3 text-center text-xs">
        * Jubilación: Los valores se actualizan según movilidad vigente (Res. SS 2/2026)
      </div>
    </div>
  )
}
